import React, { useState } from 'react'
import ProjectItem from "../ProjectItem";
import BoardColumnTitle from "./components/BoardColumnTitle";
import BoardCreateTaskBtn from "./components/BoardCreateTaskBtn";
import BoardCreateFastTask from "./components/BoardCreateFastTask";
import styles from './index.module.scss'
import {Task} from "../../../../../../types/task";
import {Project} from "../../../../../../types";
import { TaskDropTarget } from '../../../../hooks/useTaskDragAndDrop';

interface BoardColumnProps {
    boardColumn: any
    onCreateTask?: (task: any) => Promise<any>
    onTaskUpdated?: (task: Task | null, options?: { remove?: boolean }) => void
    onTaskDragEnd: () => void
    onTaskDragStart: (taskId: number) => void
    onTaskDrop: (target?: TaskDropTarget | null) => Promise<void> | void
    onTaskHover: (target: TaskDropTarget) => void
    canPersistOrder?: boolean
    draggedTaskId: number | null
    dropTarget: TaskDropTarget | null
    isDragging: boolean
    getTasks: () => void
    pendingTaskId: number | null
    tasks: Task[]
    project: Project
    projectUsers?: any[]
    parentTitles: Map<string, string>
}

export default function BoardColumn({
    boardColumn,
    canPersistOrder = true,
    draggedTaskId,
    dropTarget,
    getTasks,
    isDragging,
    onCreateTask,
    onTaskDragEnd,
    onTaskDragStart,
    onTaskDrop,
    onTaskHover,
    onTaskUpdated,
    pendingTaskId,
    project,
    projectUsers = [],
    parentTitles,
    tasks,
}: BoardColumnProps){
    const [isFastCreateOpen, setIsFastCreateOpen] = useState(false)
    const isColumnDropActive = dropTarget != null
        && dropTarget.status === boardColumn.key
        && (dropTarget.mode === 'column' || dropTarget.mode === 'status')

    const handleCreateTask = async (title: string) => {
        if (!onCreateTask) {
            return
        }

        await onCreateTask({
            assignee_files: [],
            assignee_ids: [],
            description: '',
            due_date: null,
            priority: 'medium',
            relatedTasks: [],
            status: boardColumn.key,
            task_type: 'task',
            title,
        })
        setIsFastCreateOpen(false)
    }

    return (
        <div
            className={`${styles.boardColumn} ${isColumnDropActive ? styles.boardColumnDropActive : ''}`}
            onDragOver={(event) => {
                if (!canPersistOrder || !draggedTaskId) {
                    return
                }

                event.preventDefault()
                onTaskHover({
                    mode: 'column',
                    position: 'end',
                    status: boardColumn.key,
                })
            }}
            onDrop={(event) => {
                if (!canPersistOrder || !draggedTaskId) {
                    return
                }

                event.preventDefault()
                void onTaskDrop({
                    mode: 'column',
                    position: 'end',
                    status: boardColumn.key,
                })
            }}
        >
            <BoardColumnTitle title={boardColumn.title || boardColumn.name} count={tasks.length} color={boardColumn.color}/>
            <div className={styles.boardColumn__tasks} data-board-column-tasks>
                {tasks.map((task) => (
                    <ProjectItem
                        dragPreviewPosition={dropTarget?.taskId === task.id && dropTarget.mode === 'task' ? dropTarget.position : null}
                        draggable={canPersistOrder && pendingTaskId == null}
                        getTasks={getTasks}
                        isDragging={draggedTaskId === task.id}
                        isPending={pendingTaskId === task.id}
                        key={task.id}
                        onDragEnd={onTaskDragEnd}
                        onDragStart={onTaskDragStart}
                        onDrop={onTaskDrop}
                        onHover={onTaskHover}
                        onTaskUpdated={onTaskUpdated}
                        project={project}
                        projectUsers={projectUsers}
                        task={task}
                        parentTitle={task.parent_id ? parentTitles.get(String(task.parent_id)) : undefined}
                    />
                ))}
                {isFastCreateOpen ? (
                    <BoardCreateFastTask
                        onClose={() => setIsFastCreateOpen(false)}
                        onCreate={handleCreateTask}
                    />
                ) : null}
                {(canPersistOrder && isDragging) || !tasks.length ? (
                    <div
                        className={`${styles.boardColumn__dropZone} ${isColumnDropActive ? styles.boardColumn__dropZoneActive : ''}`}
                    />
                ) : null}
            </div>
            {!isFastCreateOpen ? (
                <BoardCreateTaskBtn onClick={() => setIsFastCreateOpen(true)} />
            ) : null}
        </div>
    )
}
